/*
 * BN Cart Drawer
 * ──────────────
 * Editable slide-out cart. Opens from the header cart icon and the
 * Empire add-to-cart flyout's "View cart" link. Empire's flyout itself
 * is left untouched as the post-add confirmation.
 *
 * All interaction is delegated on `document`, so the drawer markup can be
 * freely re-rendered (via the Section Rendering API) without losing
 * listeners. Open state lives on <body> so it survives re-renders too.
 */
(function () {
  'use strict';

  var SECTION_ID = 'bn-cart-drawer';
  var OPEN_CLASS = 'bn-cart-drawer-open';
  var LOADING_CLASS = 'bn-cart-drawer-loading';
  var lastFocus = null;
  var inFlight = false;

  function drawer() { return document.querySelector('[data-bn-cart-drawer]'); }
  function panel() { return document.querySelector('[data-bn-cart-drawer-panel]'); }
  function isOpen() { return document.body.classList.contains(OPEN_CLASS); }

  function setHeaderCount(count) {
    var el = document.querySelector('[data-header-cart-count]');
    if (!el) return;
    el.setAttribute('data-header-cart-count', count > 0 ? count : '');
    el.classList.toggle('visible', count > 0);
  }

  /* Replace the drawer markup with a freshly rendered copy. Open state is on
     <body>, and listeners are delegated, so a straight node swap is safe. */
  function applySection(html) {
    if (!html) return;
    var fresh = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('[data-bn-cart-drawer]');
    var current = drawer();
    if (fresh && current) current.replaceWith(fresh);
  }

  function syncCountFromDrawer() {
    var c = document.querySelector('[data-bn-cart-drawer] .bn-cart-drawer__count');
    if (c) setHeaderCount(parseInt(c.textContent, 10) || 0);
  }

  /* Pull a fresh copy of the drawer (used on open + after a flyout add). */
  function refresh() {
    return fetch(window.location.pathname + '?sections=' + SECTION_ID, {
      credentials: 'same-origin'
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data && data[SECTION_ID]) {
          applySection(data[SECTION_ID]);
          syncCountFromDrawer();
        }
      })
      .catch(function () {});
  }

  function open() {
    var d = drawer();
    if (!d) return;
    lastFocus = document.activeElement;
    document.dispatchEvent(new Event('closeFlyouts')); // close Empire's ATC flyout if open
    document.body.classList.add(OPEN_CLASS);
    d.setAttribute('aria-hidden', 'false');
    refresh();
    var closeBtn = d.querySelector('[data-bn-cart-drawer-close]');
    if (closeBtn) setTimeout(function () { closeBtn.focus(); }, 60);
  }

  function close() {
    var d = drawer();
    document.body.classList.remove(OPEN_CLASS);
    if (d) d.setAttribute('aria-hidden', 'true');
    if (lastFocus && lastFocus.focus) {
      try { lastFocus.focus(); } catch (e) {}
    }
    lastFocus = null;
  }

  function changeLine(line, quantity) {
    if (inFlight) return;
    inFlight = true;
    document.body.classList.add(LOADING_CLASS);

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ line: line, quantity: quantity, sections: SECTION_ID })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data && data.sections && data.sections[SECTION_ID]) {
          applySection(data.sections[SECTION_ID]);
        } else {
          refresh(); // unexpected shape — resync from source
        }
        if (typeof data.item_count === 'number') setHeaderCount(data.item_count);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      })
      .catch(function () { refresh(); })
      .then(function () {
        inFlight = false;
        document.body.classList.remove(LOADING_CLASS);
      });
  }

  function trapFocus(e) {
    var p = panel();
    if (!p) return;
    var f = p.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!f.length) return;
    var first = f[0];
    var last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /* ── Delegated interaction ─────────────────────────────────────── */
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;

    // Open: header cart icon or the Empire flyout's "View cart" link
    if (e.target.closest('[data-bn-cart-drawer-trigger], [data-atc-banner-cart-button]')) {
      e.preventDefault();
      open();
      return;
    }

    // Close: overlay or close button
    if (e.target.closest('[data-bn-cart-drawer-close]')) {
      e.preventDefault();
      close();
      return;
    }

    // Quantity change / remove
    var qtyBtn = e.target.closest('[data-bn-cart-qty-change]');
    if (qtyBtn) {
      e.preventDefault();
      var row = qtyBtn.closest('[data-bn-cart-line]');
      if (!row) return;
      var line = parseInt(row.getAttribute('data-bn-cart-line'), 10);
      var qty = parseInt(qtyBtn.getAttribute('data-bn-cart-qty-change'), 10);
      if (line && qty >= 0) changeLine(line, qty);
    }
  });

  document.addEventListener('keydown', function (e) {
    if (!isOpen()) return;
    if (e.key === 'Escape') { close(); return; }
    if (e.key === 'Tab') trapFocus(e);
  });

  /* Keep the drawer current when Empire's flyout adds an item. */
  document.addEventListener('ajaxProduct:added', function () {
    refresh();
  });
})();
